/**
 * extensions/dicom-rt/src/components/LongitudinalStudy/sections/MeasurementSection.js
 *
 * RECIP 1.0 E-4 — voxel-level PSMA+ volume UI.
 *
 * E-4 changes:
 *   - PSMA Status column REMOVED
 *   - PSMA Status filter REMOVED (5 filters → 4: Body Part, Lesion Type, Specific Link, Study Date)
 *   - "PSMA+ Vol (mm³)" column ADDED (rightmost, replaces PSMA Status)
 *   - Cell rendering:
 *       number    → "123.5"  (1 decimal place)
 *       null      → "NE"   (red text, tooltip: "No liver-reference ROI…")
 *       'calculating' → "Computing…" (italic grey)
 *       undefined → "—"
 *   - Threshold banner retained (bone cutoff + soft-tissue per-tp)
 *   - SUVmin column retained (added in D-3)
 *   - filteredPsmaStatus prop no longer used; setFilteredPsmaStatus still
 *     accepted to avoid prop-drilling refactor in LongitudinalStudy.js
 *
 * Final column order (12 cols):
 *   No. | Name | Location | Long Axis | Short Axis | Volume(mm³) |
 *   SUV max | SUV mean | SUV min | Measurability | Body Part | Type | PSMA+ Vol(mm³)
 */

import React from 'react';
import PropTypes from 'prop-types';

import {
  Body,
  Container,
  Grid,
  Select,
  H6,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@platform/ui';
import module, { math } from '../../../modules/dicom-measurement/src';
import { makeSuvKey } from '../../../modules/dicom-measurement/src/suv/suvViewportBridge';
import { useSuv, useSuvComputing } from '../../../hooks/useSuvStore';
import { computeRecipThresholdsAllTps } from '../../../modules/dicom-measurement/src/assessments/getRecipThresholds';

const { round } = math;
const { guideline, getTimepoints } = module;
const {
  BODY_PARTS,
  BRAIN_LOCATION,
  getMeasurementLesionTypes,
  getBodyParts,
  getTrackingTypes,
} = guideline;

const fmt2 = v => (v == null || !isFinite(v) ? '—' : Number(v).toFixed(2));
const fmt1 = v => (v == null || !isFinite(v) ? '—' : Number(v).toFixed(1));

/**
 * RECIP 1.0 threshold banner (unchanged from D-3).
 */
const RecipThresholdBanner = ({ thresholds, timepoints }) => {
  if (!timepoints?.length) return null;
  return (
    <div
      style={{
        padding: '3px 8px',
        color: 'var(--default-color)',
        fontSize: '12px',
        opacity: 0.85,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <span title="Bone lesion threshold: absolute SUVmean cutoff (Gafita 2022 JNM).">
        Bone: {(Object.values(thresholds)[0]?.bone ?? 3.0).toFixed(1)}
      </span>
      <span style={{ margin: '0 8px', opacity: 0.4 }}>|</span>
      <span title="Soft-tissue threshold: (4.30 / liver_mean) × (liver_mean + liver_SD). N/A = no liver-reference ROI.">
        Soft-tissue:&nbsp;
        {timepoints.map((tp, i) => {
          const t = thresholds?.[tp.SeriesInstanceUID];
          const date = tp.SeriesDate || `tp${i + 1}`;
          if (!t || t.softTissue == null) {
            return (
              <span key={tp.SeriesInstanceUID} style={{ marginRight: '12px' }}>
                {date}: <span style={{ color: '#ef9a9a' }}>N/A</span>
              </span>
            );
          }
          const title = t.liverInputs
            ? `liver mean=${t.liverInputs.suvMean.toFixed(
                2
              )}, SD=${t.liverInputs.suvSD.toFixed(2)}, ROI: "${
                t.liverInputs.roiName
              }"`
            : date;
          return (
            <span
              key={tp.SeriesInstanceUID}
              style={{ marginRight: '12px' }}
              title={title}
            >
              {date}: {t.softTissue.toFixed(2)}
            </span>
          );
        })}
      </span>
    </div>
  );
};
RecipThresholdBanner.propTypes = {
  thresholds: PropTypes.object,
  timepoints: PropTypes.array,
};

/**
 * PSMA+ Volume cell renderer.
 * psmaPosVolume: number | null | 'calculating' | undefined
 */
const PsmaPosVolumeCell = ({ psmaPosVolume }) => {
  if (psmaPosVolume === 'calculating') {
    return (
      <span style={{ color: '#888', fontSize: '11px', fontStyle: 'italic' }}>
        Computing…
      </span>
    );
  }
  if (psmaPosVolume === null) {
    return (
      <span
        style={{ color: '#ef9a9a', fontSize: '12px' }}
        title="NE — no liver-reference ROI at this timepoint, or PET compute failed."
      >
        NE
      </span>
    );
  }
  if (psmaPosVolume == null) {
    return <span style={{ color: '#888' }}>—</span>;
  }
  return <span>{fmt1(psmaPosVolume)}</span>;
};
PsmaPosVolumeCell.propTypes = { psmaPosVolume: PropTypes.any };

export const MeasurementSection = ({
  imageSets,
  ROIContours,
  criteria,
  onROIUpdate,
  getFilteredROIContours,
  filteredBodyPart,
  setFilteredBodyPart,
  filteredTrackingType,
  setFilteredTrackingType,
  filteredPsmaStatus, // accepted but unused in E-state; kept for compat
  setFilteredPsmaStatus, // same
  selectedLesionNumber,
  setSelectedLesionNumber,
  timepointIndex,
  setTimepointIndex,
}) => {
  const isRecip = criteria?.guidelineId === 'RECIP10';
  const suvComputing = useSuvComputing();

  const timepoints = getTimepoints(imageSets, ROIContours);
  const filteredROIContours = getFilteredROIContours(ROIContours);

  const thresholds = isRecip
    ? computeRecipThresholdsAllTps(imageSets, ROIContours, criteria)
    : null;

  const selectedTimepoint = timepoints[Number(timepointIndex)] || timepoints[0];
  const selectedTpImageSet = selectedTimepoint
    ? imageSets.find(
        s => s.SeriesInstanceUID === selectedTimepoint.SeriesInstanceUID
      )
    : null;
  const selectedTpStructureSetUID =
    selectedTpImageSet?.rtstructSeriesInstanceUID;
  const selectedTpCtSeriesUID = selectedTimepoint?.SeriesInstanceUID;

  const onBodyPartChange = evt => setFilteredBodyPart(evt.target.value);
  const onTrackingTypeChange = evt => {
    const type = evt.target.value;
    setFilteredTrackingType(type);
    if (type !== 'selected') setSelectedLesionNumber(-1);
  };
  const onLesionNumberChange = evt =>
    setSelectedLesionNumber(Number(evt.target.value));
  const onTimepointChange = evt => setTimepointIndex(Number(evt.target.value));

  const VolumeMM = (
    <span key="volume">
      Volume(mm<sup>3</sup>)
    </span>
  );
  const PsmaVolMM = (
    <span key="psma-vol">
      PSMA+&nbsp;Vol(mm<sup>3</sup>)
    </span>
  );

  // 14 RECIP columns (adds SUV SD after SUV min)
  const columns = isRecip
    ? [
        ['No.', '4%'],
        ['Name', '8%'],
        ['Location', '6%'],
        ['Long Axis(mm)', '6%'],
        ['Short Axis(mm)', '6%'],
        [VolumeMM, '7%'],
        ['SUV max', '6%'],
        ['SUV mean', '6%'],
        ['SUV min', '6%'],
        ['SUV SD', '6%'],
        ['Body Part', '9%'],
        ['Type', '9%'],
        [PsmaVolMM, '9%'],
      ]
    : [
        ['No.', '4%'],
        ['Name', '12%'],
        ['Location', '12%'],
        ['Long Axis(mm)', '12%'],
        ['Short Axis(mm)', '12%'],
        [VolumeMM, '12%'],
        ['Measurability', '12%'],
        ['Body Part', '12%'],
        ['Type', '12%'],
      ];

  return (
    <Body>
      {isRecip && suvComputing && (
        <div
          style={{
            padding: '4px 8px',
            color: 'var(--default-color)',
            fontSize: '13px',
          }}
        >
          Computing SUV across all timepoints… values appear as each ROI
          finishes.
        </div>
      )}
      {isRecip && !suvComputing && thresholds && (
        <RecipThresholdBanner thresholds={thresholds} timepoints={timepoints} />
      )}
      <Container>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow index={-1}>
              {columns.map(([text, width], i) => (
                <TableCell
                  key={typeof text === 'string' ? text : `col-${i}`}
                  width={width}
                  header={1}
                >
                  <H6>{text}</H6>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredROIContours.map((roi, index) => {
              const lesion = selectedTimepoint?.Lesions?.find(
                d => d.ROINumber === roi.ROINumber
              );
              return (
                <Row
                  key={roi.ROINumber}
                  index={index}
                  criteria={criteria}
                  isRecip={isRecip}
                  roi={roi}
                  onROIUpdate={onROIUpdate}
                  longAxis={round(lesion?.maxLongAxisValue, -2)}
                  shortAxis={round(lesion?.maxShortAxisValue, -2)}
                  volume={round(lesion?.volume, -2)}
                  tpStructureSetUID={selectedTpStructureSetUID}
                  tpCtSeriesUID={selectedTpCtSeriesUID}
                />
              );
            })}
          </TableBody>
        </Table>
      </Container>
      <Grid container spacing={2}>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={filteredBodyPart} onChange={onBodyPartChange}>
            {getBodyParts().map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={filteredTrackingType} onChange={onTrackingTypeChange}>
            {getTrackingTypes(criteria.guidelineId).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={selectedLesionNumber} onChange={onLesionNumberChange}>
            <option key={-1} value={-1}>
              ------
            </option>
            {filteredTrackingType === 'selected' &&
              ROIContours.map(roi => (
                <option key={roi.ROINumber} value={roi.ROINumber}>
                  {roi.ROIName}
                </option>
              ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={timepointIndex} onChange={onTimepointChange}>
            {timepoints.map((tp, index) => (
              <option key={tp.SeriesDate + index} value={index}>
                {tp.SeriesDate}
              </option>
            ))}
          </Select>
        </Grid>
      </Grid>
    </Body>
  );
};
MeasurementSection.propTypes = {
  imageSets: PropTypes.array,
  ROIContours: PropTypes.array,
  criteria: PropTypes.shape({ guidelineId: PropTypes.string }),
  onROIUpdate: PropTypes.func,
  getFilteredROIContours: PropTypes.func,
  filteredBodyPart: PropTypes.string,
  setFilteredBodyPart: PropTypes.func,
  filteredTrackingType: PropTypes.string,
  setFilteredTrackingType: PropTypes.func,
  filteredPsmaStatus: PropTypes.string,
  setFilteredPsmaStatus: PropTypes.func,
  selectedLesionNumber: PropTypes.number,
  setSelectedLesionNumber: PropTypes.func,
  timepointIndex: PropTypes.number,
  setTimepointIndex: PropTypes.func,
};

const Row = ({
  index,
  criteria,
  isRecip,
  roi,
  onROIUpdate,
  longAxis,
  shortAxis,
  volume,
  tpStructureSetUID,
  tpCtSeriesUID,
}) => {
  const suvKey = isRecip
    ? makeSuvKey(tpStructureSetUID, roi.ROINumber, tpCtSeriesUID)
    : null;
  const stat = useSuv(suvKey);
  const suvComputing = useSuvComputing();

  const { ROIName, measurable, bodyPart, trackingType } = roi;
  const locKey = Object.keys(BRAIN_LOCATION).find(l => ROIName.includes(l));
  const location = BRAIN_LOCATION[locKey] || 'N/A';
  const measurability = measurable ? 'measurable' : 'non-measurable';
  const onBodyPartChange = evt =>
    onROIUpdate(roi.ROINumber, evt.target.value, trackingType);
  const onTrackingTypeChange = evt =>
    onROIUpdate(roi.ROINumber, bodyPart, evt.target.value);

  const suvSuffix = stat?.factors?.dataSource?.dose === 'fallback' ? '*' : '';
  const renderSuvCell = value => {
    if (value != null && isFinite(value)) return `${fmt2(value)}${suvSuffix}`;
    if (stat === null && suvComputing) return 'Computing…';
    return '—';
  };

  // PSMA+ volume at the selected timepoint
  const psmaPosVolume = isRecip
    ? roi.psmaPosVolumeByTimepoint?.[tpCtSeriesUID]
    : undefined;

  const dataCells = isRecip
    ? [
        index,
        ROIName,
        location,
        longAxis,
        shortAxis,
        volume,
        renderSuvCell(stat?.suvMax),
        renderSuvCell(stat?.suvMean),
        renderSuvCell(stat?.suvMin),
        renderSuvCell(stat?.suvSD),
      ]
    : [index, ROIName, location, longAxis, shortAxis, volume, measurability];

  return (
    <TableRow index={index}>
      {dataCells.map((text, i) => (
        <TableCell key={i}>
          <H6>{text}</H6>
        </TableCell>
      ))}
      <TableCell>
        <Select value={bodyPart} onChange={onBodyPartChange}>
          {[
            [BODY_PARTS.BRAIN, 'Brain'],
            [BODY_PARTS.NECK, 'Neck'],
            [BODY_PARTS.LIVER, 'Liver'],
            [BODY_PARTS.LUNG, 'Lung'],
            [BODY_PARTS.LYMPHNODE, 'Lymph Node'],
            [BODY_PARTS.PLEURA, 'Pleura'],
            [BODY_PARTS.PLEURALEFFUSION, 'Pleural Effusion'],
            [BODY_PARTS.ADRENALGLAND, 'Adrenal Gland'],
            [BODY_PARTS.BONE, 'Bone'],
            [BODY_PARTS.SOFTTISSUE, 'Soft Tissue'],
            [BODY_PARTS.OTHERS, 'Others'],
          ].map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <Select value={trackingType} onChange={onTrackingTypeChange}>
          {getMeasurementLesionTypes(criteria.guidelineId).map(i => (
            <option key={i.id} value={i.id}>
              {i.label}
            </option>
          ))}
        </Select>
      </TableCell>
      {isRecip && (
        <TableCell>
          <PsmaPosVolumeCell psmaPosVolume={psmaPosVolume} />
        </TableCell>
      )}
    </TableRow>
  );
};
Row.propTypes = {
  index: PropTypes.number,
  criteria: PropTypes.object,
  isRecip: PropTypes.bool,
  roi: PropTypes.object,
  onROIUpdate: PropTypes.func,
  longAxis: PropTypes.number,
  shortAxis: PropTypes.number,
  volume: PropTypes.number,
  tpStructureSetUID: PropTypes.string,
  tpCtSeriesUID: PropTypes.string,
};
